import React from "react";
import moment from "moment";
import {VFlex} from "components/_defaultComponents/Flex/VFlex";
import {Collapse, DatePicker, Radio, Tooltip} from "antd";
import trTR from "antd/es/date-picker/locale/tr_TR";
import {InfoCircleOutlined} from "@ant-design/icons";
import {
    CityFilterWithIstanbulContinents,
    DrivingLicenseFilter,
    ManagerCompanyFilter,
    MotorTypeFilter,
    PersonalCompanySetupStatusFilter,
    WorkingModelTypeFilter
} from "components/_defaultComponents/FilterComponents";
import DistrictFilter from "components/_defaultComponents/FilterComponents/DistrictFilter";
import {useTranslation} from "react-i18next";
import {SelectField, SingleMonthRangePickerStyle} from "../newCourierStyles";
import {
    activationSteps,
    activationSubSteps,
    courierEmploymentTypes,
    courierTypes,
    trainingStatuses
} from "constants/constants.js";
import {useCourierFilter} from "../context/useCourierFilter";
import CriminalRecordWithRadioButton from "./CriminalRecordWithRadioButton";
import {ZoneManagementSelect} from "components/_defaultComponents/ZoneManagementSelect";
import {ZoneSelect} from "components/_defaultComponents/ZoneSelect";

const CourierPageFilter = () => {
    const {t} = useTranslation(["courierPage", "common", "activationSteps", "activationSubSteps"]);

    const {
        dispatch,
        stores,
        state,
        renderCallStatusOptions,
        isAgency,
        isAdmin
    } = useCourierFilter();

    const handleDateChange = value => {
        dispatch({
            type: "REPLACE_STATE",
            payload: {
                ...state,
                selectedDateRange: value
            }
        });
    };

    const handlePreferredCityChange = value => {
        dispatch({
            type: "REPLACE_STATE",
            payload: {
                ...state,
                currentPage: 1,
                preferredCity: value?.toLocaleLowerCase("tr-TR"),
                preferredDistricts: []
            },
            isAppend: true
        });
    };

    const handlePreferredContinentChange = value => {
        dispatch({type: "setPreferredContinent", payload: value});
    };

    const handlePreferredDistrictsChange = value => {
        dispatch({
            type: "REPLACE_STATE",
            payload: {
                ...state,
                currentPage: 1,
                preferredDistricts: value || []
            },
            isAppend: true
        });
    };

    const handleCallStatusChange = value => {
        dispatch({
            type: "REPLACE_STATE",
            payload: {
                ...state,
                currentPage: 1,
                callStatus: value
            }
        });
    };

    const handleZoneManagementIdChange = value => {
        dispatch({
            type: "REPLACE_STATE",
            payload: {
                ...state,
                currentPage: 1,
                zoneManagementId: value
            }
        });
    };

    const handleZoneIdChange = value => {
        dispatch({
            type: "REPLACE_STATE",
            payload: {
                ...state,
                currentPage: 1,
                zoneId: value
            }
        });
    };

    const handleActivationStepChange = value => {
        dispatch({
            type: "REPLACE_STATE",
            payload: {
                ...state,
                currentPage: 1,
                activationStep: value
            }
        });
    };

    const handleActivationSubStepChange = value => {
        dispatch({
            type: "REPLACE_STATE",
            payload: {
                ...state,
                currentPage: 1,
                activationSubStep: value,
                autoValidationStatus:
                    value !== "documentsInReview" && value !== "documentsRejected"
                        ? "all"
                        : state.autoValidationStatus,
                // VGT-216-1: İşe Alım Durumu değiştiğinde ilgisiz alt-filtreler sıfırlanır.
                trainingStatus: value !== "onTheJobTraining" ? "all" : state.trainingStatus,
                contractSigningStatus:
                    value !== "equipmentCompletion" ? "all" : state.contractSigningStatus
            }
        });
    };

    const handleTrainingStatusChange = value => {
        dispatch({
            type: "REPLACE_STATE",
            payload: {
                ...state,
                currentPage: 1,
                trainingStatus: value
            }
        });
    };

    const handleLeaveStatusChange = value => {
        dispatch({
            type: "REPLACE_STATE",
            payload: {
                ...state,
                currentPage: 1,
                leaveStatus: value || "all"
            }
        });
    };

    const handleActivityChange = value => {
        dispatch({
            type: "REPLACE_STATE",
            payload: {
                ...state,
                currentPage: 1,
                activity: value
            }
        });
    };

    const handleCityChange = value => {
        dispatch({
            type: "REPLACE_STATE",
            payload: {
                ...state,
                currentPage: 1,
                cityId: value,
                districts: []
            }
        });
    };

    const handleDistrictsChange = value => {
        dispatch({
            type: "REPLACE_STATE",
            payload: {
                ...state,
                currentPage: 1,
                districts: value || []
            }
        });
    };

    const handleDrivingLicenseIdsChange = value => {
        dispatch({
            type: "REPLACE_STATE",
            payload: {
                ...state,
                currentPage: 1,
                drivingLicenseIds: value || []
            },
            isAppend: true
        });
    };

    const handleMotorTypeIdsChange = value => {
        dispatch({
            type: "REPLACE_STATE",
            payload: {
                ...state,
                currentPage: 1,
                motorTypeIds: value || []
            },
            isAppend: true
        });
    };

    const handlePersonalCompanySetupStatusChange = value => {
        dispatch({
            type: "REPLACE_STATE",
            payload: {
                ...state,
                currentPage: 1,
                personalCompanySetupStatus: value
            }
        });
    };

    const handleContractSigningStatusChange = value => {
        dispatch({
            type: "REPLACE_STATE",
            payload: {
                ...state,
                currentPage: 1,
                contractSigningStatus: value
            }
        });
    };

    const handleTypesChange = value => {
        dispatch({
            type: "REPLACE_STATE",
            payload: {
                ...state,
                currentPage: 1,
                types: value || []
            },
            isAppend: true
        });
    };

    const handleEmploymentTypeChange = value => {
        dispatch({
            type: "REPLACE_STATE",
            payload: {
                ...state,
                currentPage: 1,
                employmentType: value
            }
        });
    };

    const handleManagerCompanyChange = value => {
        dispatch({
            type: "REPLACE_STATE",
            payload: {
                ...state,
                currentPage: 1,
                managerCompany: value
            }
        });
    };

    const handleWorkingModelIdChange = value => {
        dispatch({
            type: "REPLACE_STATE",
            payload: {
                ...state,
                currentPage: 1,
                workingModelId: value || []
            },
            isAppend: true
        });
    };

    const handleAutoValidationStatusChange = value => {
        dispatch({
            type: "REPLACE_STATE",
            payload: {
                ...state,
                currentPage: 1,
                autoValidationStatus: value || "all"
            }
        });
    };

    const handleIs20OldChange = e => {
        dispatch({
            type: "REPLACE_STATE",
            payload: {
                ...state,
                currentPage: 1,
                is20Old: e.target.value
            }
        });
    };

    const handleIs20OldClick = value => {
        if (state.is20Old === value) {
            dispatch({
                type: "REPLACE_STATE",
                payload: {
                    ...state,
                    currentPage: 1,
                    is20Old: undefined
                }
            });
        }
    };

    // VGT-329: Acente kullanıcısı "Onaylı" yerine "Aktif" ifadesini görür.
    const renderActivationStepLabel = oStatus =>
        isAgency && oStatus.value === "approved"
            ? "Aktif"
            : t(oStatus.translation_key, {ns: "activationSteps"});

    const renderLeaveStatusFilter = () => (
        <VFlex vertical minWidth={150} flex>
            <h5>İzin Durumu</h5>
            <SelectField
                style={{marginBottom: "0px"}}
                optionFilterProp="label"
                value={state.leaveStatus || "all"}
                onChange={handleLeaveStatusChange}
                data-testid="leaveStatus"
            >
                <SelectField.Option value="all" data-testid="leaveStatus_options">
                    Tümü
                </SelectField.Option>
                <SelectField.Option value="onLeave" data-testid="leaveStatus_options">
                    İzinde Olanlar
                </SelectField.Option>
            </SelectField>
        </VFlex>
    );

    const dummyGapForFilters = () => {
        if (["documentsInReview", "documentsRejected"].includes(state.activationSubStep)) {
            return [1];
        } else {
            return [1, 2];
        }
    };

    return (
        <VFlex vertical gap={16} fullWidth>
            <SingleMonthRangePickerStyle />
            <VFlex gap={16} align="flex-end" wrap={true} flex>
                {/* VGT-329: RangePicker'ın min genişliği kapsayıcıya da verilir, aksi halde
                    taşıp yanındaki filtreyle arasındaki boşluğu yiyor. */}
                <VFlex vertical minWidth={200} flex>
                    <VFlex minWidth={150} flex gap={4}>
                        <h5>Başvuru Tarihi</h5>
                        {!isAgency && (
                            <Tooltip title="Son 30 gün içerisinde başvuranları gösterir.">
                                <InfoCircleOutlined />
                            </Tooltip>
                        )}
                    </VFlex>
                    <DatePicker.RangePicker
                        style={{width: "100%", minWidth: 200}}
                        placeholder={["Başlangıç Tarihi", "Bitiş Tarihi"]}
                        locale={trTR}
                        dropdownClassName="single-month-range-picker"
                        defaultPickerValue={[moment().startOf("month"), moment().startOf("month")]}
                        onChange={handleDateChange}
                        value={
                            state.selectedDateRange?.length === 2
                                ? state.selectedDateRange
                                : undefined
                        }
                        allowClear
                        suffixIcon={null}
                        format="DD/MM/YYYY"
                    />
                </VFlex>
                <VFlex vertical minWidth={150} flex>
                    <h5>Çalışmak İstenen Şehir</h5>
                    <CityFilterWithIstanbulContinents
                        showLabel={false}
                        allowClear={true}
                        showAllOption={true}
                        forceShowAllOption={true}
                        selectMarginBottom={"0px"}
                        value={state.preferredCity}
                        onChange={handlePreferredCityChange}
                        onContinentChange={handlePreferredContinentChange}
                    />
                </VFlex>
                <VFlex vertical minWidth={150} flex>
                    <h5>Çalışmak İstenen İlçe</h5>
                    <DistrictFilter
                        allowClear={true}
                        selectMarginBottom={"0px"}
                        cityId={state.preferredCity}
                        continent={state.preferredContinent}
                        disabled={!state.preferredCity}
                        value={state.preferredDistricts}
                        showLabel={false}
                        showAllOption={false}
                        mode="multiple"
                        optionMappingKey="_id"
                        onChange={handlePreferredDistrictsChange}
                    />
                </VFlex>
                {/* Call Status */}
                <VFlex vertical minWidth={150} flex>
                    <h5>{t("fields.callStatus", {ns: "common"})}</h5>
                    <SelectField
                        style={{marginBottom: "0px"}}
                        allowClear={true}
                        value={state.callStatus}
                        onChange={handleCallStatusChange}
                    >
                        {renderCallStatusOptions({callStatuses: state.callStatuses})}
                    </SelectField>
                </VFlex>
                <VFlex vertical minWidth={150} flex>
                    <h5>{t("fields.activationStep", {ns: "common"})}</h5>
                    <SelectField
                        allowClear={true}
                        style={{marginBottom: "0px"}}
                        optionFilterProp="label"
                        value={state.activationStep}
                        onChange={handleActivationStepChange}
                        placeholder={t("filters.addFilter", {ns: "common"})}
                        data-testid="activationStep"
                    >
                        {activationSteps.map(oStatus => (
                            <SelectField.Option
                                key={oStatus.value}
                                value={oStatus.value}
                                label={renderActivationStepLabel(oStatus)}
                                data-testid="courierStatus_options"
                            >
                                {renderActivationStepLabel(oStatus)}
                            </SelectField.Option>
                        ))}
                    </SelectField>
                </VFlex>
                <VFlex vertical minWidth={150} flex>
                    <h5>İşe Alım Durumu</h5>
                    <SelectField
                        allowClear={true}
                        style={{marginBottom: "0px"}}
                        optionFilterProp="label"
                        value={state.activationSubStep}
                        onChange={handleActivationSubStepChange}
                        placeholder={t("filters.addFilter", {ns: "common"})}
                        data-testid="activationSubStep"
                    >
                        {activationSubSteps
                            .filter(oStatus =>
                                [
                                    "all",
                                    "newApplication",
                                    "documentsUpload",
                                    "documentsInReview",
                                    "documentsRejected",
                                    "projectWaiting",
                                    "onTheJobTraining",
                                    "equipmentCompletion",
                                    "shiftWaiting",
                                    "approved",
                                    "pendingEvaluation",
                                    "pendingTermination",
                                    "terminationCompleted",
                                    "blocked",
                                    "rejected"
                                ].includes(oStatus.value)
                            )
                            .map(oStatus => (
                                <SelectField.Option
                                    key={oStatus.value}
                                    value={oStatus.value}
                                    label={t(oStatus.translation_key, {
                                        ns: "activationSubSteps"
                                    })}
                                    data-testid="courierSubStatus_options"
                                >
                                    {t(oStatus.translation_key, {
                                        ns: "activationSubSteps"
                                    })}
                                </SelectField.Option>
                            ))}
                    </SelectField>
                </VFlex>
                {isAgency && (
                    <VFlex vertical minWidth={150} flex>
                        <h5>Ekipman Sözleşme Durumu</h5>
                        <SelectField
                            style={{marginBottom: "0px"}}
                            optionFilterProp="label"
                            value={state.contractSigningStatus || "all"}
                            onChange={handleContractSigningStatusChange}
                            disabled={state.activationSubStep !== "equipmentCompletion"}
                            data-testid="contractSigningStatus"
                        >
                            <SelectField.Option
                                value="all"
                                data-testid="contractSigningStatus_options"
                            >
                                Tümü
                            </SelectField.Option>
                            <SelectField.Option
                                value="notSent"
                                data-testid="contractSigningStatus_options"
                            >
                                Gönderilmedi
                            </SelectField.Option>
                            <SelectField.Option
                                value="pending"
                                data-testid="contractSigningStatus_options"
                            >
                                İmza Bekliyor
                            </SelectField.Option>
                            <SelectField.Option
                                value="signed"
                                data-testid="contractSigningStatus_options"
                            >
                                İmzalandı
                            </SelectField.Option>
                            <SelectField.Option
                                value="approved"
                                data-testid="contractSigningStatus_options"
                            >
                                Onaylandı
                            </SelectField.Option>
                        </SelectField>
                    </VFlex>
                )}
                {isAgency && renderLeaveStatusFilter()}
                {!isAgency && state.activationSubStep === "onTheJobTraining" && (
                    <VFlex vertical minWidth={150} flex>
                        <h5>Eğitim Durumu</h5>
                        <SelectField
                            allowClear={true}
                            style={{marginBottom: "0px"}}
                            optionFilterProp="label"
                            value={state.trainingStatus}
                            onChange={handleTrainingStatusChange}
                            placeholder={t("filters.addFilter", {ns: "common"})}
                            data-testid="trainingStatus"
                        >
                            {trainingStatuses
                                .filter(status => !["completed"].includes(status?.value))
                                .map(status => (
                                    <SelectField.Option
                                        key={status.value}
                                        value={status.value}
                                        label={status.name}
                                        data-testid="trainingStatus_options"
                                    >
                                        {status.name}
                                    </SelectField.Option>
                                ))}
                        </SelectField>
                    </VFlex>
                )}
            </VFlex>
            {!isAgency && (
                <Collapse style={{width: "100%"}}>
                    <Collapse.Panel header="Diğer Filtreler" key="other-filters">
                        <VFlex vertical gap={16}>
                            <VFlex gap={16} wrap fullWidth justify="space-between">
                                {stores.meStore.checkRole("notVisibleForCompanyUser") && (
                                    <VFlex vertical minWidth={150} flex>
                                        <h5>{t("fields.courierActivity", {ns: "common"})}</h5>
                                        <SelectField
                                            allowClear={true}
                                            style={{marginBottom: "0px"}}
                                            value={state.activity}
                                            data-testid="courierActivity"
                                            onChange={handleActivityChange}
                                        >
                                            <SelectField.Option
                                                value="all"
                                                data-testid="courierActivity_option"
                                            >
                                                {t("filters.all", {ns: "common"})}
                                            </SelectField.Option>
                                            <SelectField.Option
                                                value="enabled"
                                                data-testid="courierActivity_option"
                                            >
                                                {t("courierActivityStatuses.activeCouriers", {
                                                    ns: "courierPage"
                                                })}
                                            </SelectField.Option>
                                            <SelectField.Option
                                                value="disabled"
                                                data-testid="courierActivity_option"
                                            >
                                                {t("courierActivityStatuses.inactiveCouriers", {
                                                    ns: "courierPage"
                                                })}
                                            </SelectField.Option>
                                        </SelectField>
                                    </VFlex>
                                )}
                                {/* Zone Management Filter */}
                                <VFlex vertical minWidth={150} flex>
                                    <ZoneManagementSelect
                                        allowClear
                                        selectMarginBottom="0px"
                                        value={state.zoneManagementId}
                                        onChange={handleZoneManagementIdChange}
                                    />
                                </VFlex>
                                {/* Zone Filter */}
                                <VFlex vertical minWidth={150} flex>
                                    <ZoneSelect
                                        allowClear
                                        marginBottom="0px"
                                        value={state.zoneId}
                                        onChange={handleZoneIdChange}
                                    />
                                </VFlex>
                                {/* City Filter */}
                                <VFlex vertical minWidth={150} flex>
                                    <CityFilterWithIstanbulContinents
                                        allowClear
                                        selectMarginBottom="0px"
                                        value={state.cityId}
                                        onContinentChange={value => {
                                            dispatch({type: "setContinent", payload: value});
                                        }}
                                        onChange={handleCityChange}
                                        style={{width: "100%"}}
                                    />
                                </VFlex>

                                {/* District Filter */}
                                <VFlex vertical minWidth={150} flex>
                                    <DistrictFilter
                                        allowClear
                                        selectMarginBottom="0px"
                                        cityId={state.cityId}
                                        continent={state.continent}
                                        disabled={!state.cityId}
                                        mode="multiple"
                                        value={state.districts}
                                        onChange={handleDistrictsChange}
                                        style={{width: "100%"}}
                                        optionMappingKey="name"
                                    />
                                </VFlex>
                                <VFlex vertical minWidth={150} flex>
                                    <DrivingLicenseFilter
                                        mode="multiple"
                                        style={{marginBottom: "0px"}}
                                        allowClear={true}
                                        value={state.drivingLicenseIds}
                                        showAllOption={false}
                                        onChange={handleDrivingLicenseIdsChange}
                                    />
                                </VFlex>
                            </VFlex>
                            <VFlex gap={16} wrap fullWidth justify="space-between">
                                {/* Motor Type Filter */}
                                <VFlex vertical minWidth={150} flex>
                                    <MotorTypeFilter
                                        mode="multiple"
                                        style={{marginBottom: "0px"}}
                                        allowClear={true}
                                        value={state.motorTypeIds}
                                        showAllOption={false}
                                        onChange={handleMotorTypeIdsChange}
                                    />
                                </VFlex>
                                {/* Personal Company Setup Status */}
                                <VFlex vertical minWidth={150} flex>
                                    <PersonalCompanySetupStatusFilter
                                        allowClear
                                        selectMarginBottom="0px"
                                        value={state.personalCompanySetupStatus}
                                        onChange={handlePersonalCompanySetupStatusChange}
                                    />
                                </VFlex>
                                {/* Çalışma Şekli (Employment Type) */}
                                <VFlex vertical minWidth={150} flex>
                                    <h5>Çalışma Şekli</h5>
                                    <SelectField
                                        allowClear={true}
                                        style={{marginBottom: "0px"}}
                                        optionFilterProp="label"
                                        value={state.employmentType}
                                        onChange={handleEmploymentTypeChange}
                                    >
                                        {courierEmploymentTypes.map(type => (
                                            <SelectField.Option key={type.value} value={type.value}>
                                                {type.name}
                                            </SelectField.Option>
                                        ))}
                                    </SelectField>
                                </VFlex>
                                {/* Yönetici Firma (sadece admin) */}
                                {isAdmin && (
                                    <VFlex vertical minWidth={150} flex>
                                        <ManagerCompanyFilter
                                            selectMarginBottom="0px"
                                            value={state.managerCompany}
                                            onChange={handleManagerCompanyChange}
                                        />
                                    </VFlex>
                                )}
                                {/* Courier Type */}
                                <VFlex vertical minWidth={150} flex>
                                    <h5>{t("fields.courierType", {ns: "common"})}</h5>
                                    <SelectField
                                        allowClear={true}
                                        style={{marginBottom: "0px"}}
                                        optionFilterProp="label"
                                        value={state.types}
                                        onChange={handleTypesChange}
                                        mode="multiple"
                                        placeholder={t("filters.addFilter", {ns: "common"})}
                                        data-testid="courierTypes"
                                    >
                                        {courierTypes.map(ty => (
                                            <SelectField.Option
                                                key={ty.value}
                                                value={ty.value}
                                                label={t(ty.translation_key, {
                                                    ns: "courierTypes"
                                                })}
                                                data-testid="courierTypes_option"
                                            >
                                                {t(ty.translation_key, {ns: "courierTypes"})}
                                            </SelectField.Option>
                                        ))}
                                    </SelectField>
                                </VFlex>
                                <VFlex vertical minWidth={150} flex>
                                    <WorkingModelTypeFilter
                                        disabled={false}
                                        mode="multiple"
                                        style={{marginBottom: "0px"}}
                                        allowClear={true}
                                        value={state.workingModelId}
                                        showAllOption={false}
                                        onChange={handleWorkingModelIdChange}
                                    />
                                </VFlex>
                            </VFlex>
                            <VFlex gap={16} wrap fullWidth justify="space-between">
                                {["documentsInReview", "documentsRejected"].includes(
                                    state.activationSubStep
                                ) && (
                                    <VFlex vertical minWidth={150} flex>
                                        <VFlex minWidth={150} flex gap={4}>
                                            <h5>Otomatik Onay Durumu</h5>
                                            <Tooltip title="Belgelerin otomatik onaylanma durumunun kontrol edildiği filtredir.">
                                                <InfoCircleOutlined />
                                            </Tooltip>
                                        </VFlex>
                                        <SelectField
                                            allowClear={true}
                                            style={{marginBottom: "0px"}}
                                            value={state.autoValidationStatus}
                                            onChange={handleAutoValidationStatusChange}
                                            placeholder={t("filters.addFilter", {
                                                ns: "common"
                                            })}
                                            data-testid="autoValidationStatus"
                                        >
                                            <SelectField.Option value="all">
                                                Hepsi
                                            </SelectField.Option>
                                            <SelectField.Option value="allApproved">
                                                Hepsi Onaylandı
                                            </SelectField.Option>
                                            <SelectField.Option value="notAllApproved">
                                                Hepsi Onaylanmadı
                                            </SelectField.Option>
                                            <SelectField.Option value="pendingManualApproval">
                                                Manuel Onay Bekleyenler
                                            </SelectField.Option>
                                        </SelectField>
                                    </VFlex>
                                )}
                                {renderLeaveStatusFilter()}
                                <CriminalRecordWithRadioButton />
                                {/* 20 Old */}
                                <VFlex vertical minWidth={150} flex>
                                    <h5>{t("fields.20old", {ns: "common"})}</h5>
                                    <Radio.Group
                                        value={state.is20Old}
                                        onChange={handleIs20OldChange}
                                    >
                                        <Radio
                                            value={true}
                                            onClick={() => handleIs20OldClick(true)}
                                        >
                                            20 yaş üstü
                                        </Radio>
                                        <Radio
                                            value={false}
                                            onClick={() => handleIs20OldClick(false)}
                                        >
                                            20 yaş ve altı
                                        </Radio>
                                    </Radio.Group>
                                </VFlex>
                                {dummyGapForFilters?.()?.map((_, idx) => (
                                    <VFlex key={idx.toString()} vertical minWidth={150} flex />
                                ))}
                            </VFlex>
                        </VFlex>
                    </Collapse.Panel>
                </Collapse>
            )}
        </VFlex>
    );
};

export default CourierPageFilter;
